import { Node, mergeAttributes } from "@tiptap/core";
import { textblockTypeInputRule } from "prosemirror-inputrules";

type Level = 1 | 2 | 3 | 4 | 5 | 6;

export interface HeadingOptions {
  levels: Level[];
  HTMLAttributes: Record<string, any>;
}

declare module "@tiptap/core" {
  interface Commands<ReturnType> {
    heading: {
      /**
       * Set a heading node
       */
      setHeading: (attributes: { level: Level }) => ReturnType;
      /**
       * Toggle a heading node
       */
      toggleHeading: (attributes: { level: Level }) => ReturnType;
    };
  }
}

export const TiptapHeading = Node.create<HeadingOptions>({
  name: "heading",

  defaultOptions: {
    levels: [1, 2, 3, 4, 5, 6],
    HTMLAttributes: {},
  },

  content: "inline*",

  group: "block",

  defining: true,

  addAttributes() {
    return {
      level: {
        default: 1,
        rendered: false,
      },
    };
  },

  parseHTML() {
    return this.options.levels.map((level: Level) => ({
      tag: `h${level}`,
      attrs: { level },
    }));
  },

  renderHTML({ node, HTMLAttributes }) {
    const hasLevel = this.options.levels.includes(node.attrs.level);
    const level = hasLevel ? node.attrs.level : this.options.levels[0];

    return [
      `h${level}`,
      mergeAttributes(this.options.HTMLAttributes, HTMLAttributes),
      0,
    ];
  },

  addCommands() {
    return {
      setHeading:
        (attributes) =>
        ({ commands, editor, chain }) => {
          if (!this.options.levels.includes(attributes.level)) {
            return false;
          }

          return commands.setNode("heading", attributes);
        },
      toggleHeading:
        (attributes) =>
        ({ commands }) => {
          if (!this.options.levels.includes(attributes.level)) {
            return false;
          }

          return commands.toggleNode("heading", "paragraph", attributes);
        },
    };
  },

  addKeyboardShortcuts() {
    return this.options.levels.reduce(
      (items, level) => ({
        ...items,
        ...{
          [`Mod-Alt-${level}`]: () =>
            this.editor.commands.toggleHeading({ level }),
        },
      }),
      {}
    );
  },

  addInputRules() {
    // This is the rules that support "#", "##", "###".
    // For this to work, I need to "hijack" the handler function when
    // a # is detected

    // For each level
    return this.options.levels.map((level) => {
      // This is the original function return, I need to invoke it to properly get the
      // inputRule
      const inputRule = textblockTypeInputRule(
        new RegExp(`^(#{1,${level}})\\s$`),
        this.type,
        { level }
      );

      return {
        // Explode all the input rule attributes, except the handler (that will be
        // invoked when the Regex above has a match)
        ...inputRule,
        handler: (...args) => {
          // Start the command that makes the current node become h1, h2 and h3
          const chain = this.editor.chain().setHeading({ level });

          // Check if there's a current alignment in the node, add it to the chain,
          // otherwise the heading will lose alignment
          if (this.editor.isActive({ textAlign: "left" }))
            chain.setTextAlign("left");

          if (this.editor.isActive({ textAlign: "center" }))
            chain.setTextAlign("center");

          if (this.editor.isActive({ textAlign: "right" }))
            chain.setTextAlign("right");

          // Run the chain (set heading -> keep alignment)
          chain.run();

          // After the chain is run, extract a transaction from the editor and
          // add the "deleteRange" to it. This removes the "# ", "## ", "### "
          // before the text that will become heading
          const { state } = this.editor;
          const { tr } = state;

          const regexMatchFrom = args[2];
          const regexMatchTo = args[3];

          tr.deleteRange(regexMatchFrom, regexMatchTo);

          // Return the transaction, required by the InputRules.handler() function
          return tr;
        },
      };
    });
  },
});
